import { useEffect, useState } from "react";
import api, { formatBRL } from "@/lib/api";
import { BadgePercent, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function GestorFinanceiro() {
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ user_id: "", amount: "", description: "", due_date: "", method: "pix" });

  const load = () => api.get("/invoices").then(r=>setInvoices(r.data));
  useEffect(()=>{ load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", { ...form, amount: parseFloat(form.amount || 0) });
      toast.success("Fatura emitida (boleto/PIX simulados)");
      setForm({ user_id: "", amount: "", description: "", due_date: "", method: "pix" });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const totalPendente = invoices.filter(i=>i.status==="pendente").reduce((s,i)=>s+i.amount,0);
  const totalPago = invoices.filter(i=>i.status==="pago").reduce((s,i)=>s+i.amount,0);

  return (
    <div className="space-y-6" data-testid="gestor-financeiro-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0E2A5C] flex items-center gap-2"><BadgePercent className="h-7 w-7" /> Financeiro</h1>
          <p className="text-sm text-slate-500">Emissão de boletos, carnês e PIX (simulado)</p>
        </div>
        <button onClick={()=>setShowForm(s=>!s)} className="btn-primary text-sm" data-testid="add-invoice-button">
          {showForm ? <><X className="h-4 w-4" /> Fechar</> : <><Plus className="h-4 w-4" /> Nova fatura</>}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="kpi-tile" data-testid="kpi-pago">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Recebido</div>
          <div className="font-display text-2xl font-black text-emerald-600">{formatBRL(totalPago)}</div>
        </div>
        <div className="kpi-tile" data-testid="kpi-pendente-fin">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Em aberto</div>
          <div className="font-display text-2xl font-black text-amber-600">{formatBRL(totalPendente)}</div>
        </div>
        <div className="kpi-tile" data-testid="kpi-total-fat">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total faturado</div>
          <div className="font-display text-2xl font-black text-[#0E2A5C]">{formatBRL(totalPago + totalPendente)}</div>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-soft p-6 grid sm:grid-cols-2 gap-3" data-testid="invoice-form">
          <input required placeholder="ID do cliente (UUID)" value={form.user_id} onChange={(e)=>setForm({...form, user_id: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invoice-user" />
          <input required type="number" step="0.01" placeholder="Valor (R$)" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invoice-amount" />
          <input required placeholder="Descrição" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="sm:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invoice-desc" />
          <input required type="date" value={form.due_date} onChange={(e)=>setForm({...form, due_date: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invoice-due" />
          <select value={form.method} onChange={(e)=>setForm({...form, method: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invoice-method">
            <option value="pix">PIX</option><option value="boleto">Boleto</option><option value="carne">Carnê</option>
          </select>
          <button type="submit" className="btn-primary sm:col-span-2" data-testid="invoice-create-submit">Emitir fatura</button>
        </form>
      )}

      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Descrição</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Cliente ID</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Vencimento</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Valor</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Método</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map(inv => (
              <tr key={inv.id} data-testid={`fin-row-${inv.id}`}>
                <td className="px-5 py-3 font-semibold text-[#0E2A5C]">{inv.description}</td>
                <td className="px-5 py-3 font-mono text-xs text-slate-500">{inv.user_id?.slice(0,8)}...</td>
                <td className="px-5 py-3">{new Date(inv.due_date).toLocaleDateString("pt-BR")}</td>
                <td className="px-5 py-3 font-bold">{formatBRL(inv.amount)}</td>
                <td className="px-5 py-3 uppercase text-xs">{inv.method}</td>
                <td className="px-5 py-3">
                  {inv.status === "pago"
                    ? <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700">Pago</span>
                    : <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-700">Pendente</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
